import { useOrganization, useOrganizationList } from "@clerk/tanstack-react-start"
import { api } from "@stoneflow/backend/convex/_generated/api"
import type { Id } from "@stoneflow/backend/convex/_generated/dataModel"
import { createFileRoute } from "@tanstack/react-router"
import { useMutation, useQuery } from "convex/react"
import { BookOpen } from "lucide-react"
import { LogSelector } from "#/components/log-selector"
import { RouteError } from "#/components/route-error"
import { LogSelectorSkeleton } from "#/components/skeletons/log-selector-skeleton"
import { TenantSelector } from "#/components/tenant-selector"
import { Empty, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from "#/components/ui/empty"
import { useAppNavigate } from "#/hooks/use-app-navigate"
import { useOrgInit } from "#/hooks/useOrgInit"
import { trackEvent } from "#/lib/posthog"

export const Route = createFileRoute("/_authenticated/stonelog/select-log")({
	errorComponent: RouteError,
	component: SelectLog,
})

function SelectLog() {
	const { isReady } = useOrgInit()
	// Gate on `isReady` so the query skips during a tenant switch (returning undefined)
	// and we re-fire it once the new org's settings are in place — instead of erroring
	// against a half-switched JWT.
	const logs = useQuery(api.stonelog.logs.listMyLogs, isReady ? {} : "skip")
	const switchLog = useMutation(api.stonelog.logs.switchLog)
	const navigate = useAppNavigate()

	const { organization } = useOrganization()
	const {
		userMemberships,
		setActive,
		isLoaded: isOrgListLoaded,
	} = useOrganizationList({
		userMemberships: { infinite: true, pageSize: 100 },
	})

	const tenants = isOrgListLoaded
		? (userMemberships.data ?? []).map((m) => ({
				id: m.organization.id,
				name: m.organization.name,
			}))
		: []

	const handleSelectTenant = (tenantId: string) => {
		if (!setActive || tenantId === organization?.id) return
		setActive({ organization: tenantId }).catch(console.error)
		trackEvent("tenant_switched", { org_id: tenantId })
	}

	const handleSelectLog = async (logId: string) => {
		await switchLog({ logId: logId as Id<"logs"> })
		navigate({ to: "/stonelog/dashboard" })
	}

	const showTenants = tenants.length > 1

	return (
		<div className="mx-auto max-w-4xl p-6 space-y-8">
			<div>
				<h1>Select a Log</h1>
				<p className="mt-1 text-sm text-foreground-secondary">
					{showTenants
						? "Pick a tenant, then choose which log you want to work with."
						: "Choose which log you want to work with."}
				</p>
			</div>

			{showTenants && (
				<section className="space-y-3">
					<h2 className="text-sm font-medium">Tenant</h2>
					<TenantSelector
						tenants={tenants}
						currentTenantId={organization?.id}
						onSelect={handleSelectTenant}
					/>
				</section>
			)}

			<section className="space-y-3">
				{showTenants && <h2 className="text-sm font-medium">Logs</h2>}
				{logs === undefined ? (
					<LogSelectorSkeleton />
				) : logs.length === 0 ? (
					<Empty>
						<EmptyHeader>
							<EmptyMedia variant="icon">
								<BookOpen />
							</EmptyMedia>
							<EmptyTitle>No logs available</EmptyTitle>
							<EmptyDescription>
								{showTenants
									? "This tenant has no logs assigned to you. Pick another tenant or ask your administrator to assign you to a log."
									: "Ask your administrator to assign you to a log."}
							</EmptyDescription>
						</EmptyHeader>
					</Empty>
				) : (
					<LogSelector logs={logs} onSelect={handleSelectLog} />
				)}
			</section>
		</div>
	)
}
